import {
  Button,
  Flex,
  FormControl,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spacer,
  Text,
} from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import {
  CloseCircleOutline as CloseCircle,
  CheckOutline,
  DropdownMultiSelectInput,
} from '@aslanpay/portal-common-lib';

import { AddBenefitNoCategoriesAssignedModalProps } from './AddBenefitNoCategoriesAssignedModal.types';

function RadioOption({
  title,
  description,
  value,
  children = null,
  setRadioValue,
  radioValue,
}: {
  title: string;
  description: string;
  value: 'uncategorized' | 'addCategory';
  radioValue: 'uncategorized' | 'addCategory';
  children?: ReactNode;
  setRadioValue: React.Dispatch<React.SetStateAction<'uncategorized' | 'addCategory'>>;
}) {
  return (
    <Flex
      direction="row"
      mb="6px"
      gap="16px"
      justifyContent="start"
      alignItems="start"
      onClick={() => setRadioValue(value)}
    >
      <Radio
        value={value}
        mb="16px"
        isChecked={radioValue === value}
        onChange={() => setRadioValue(value)}
        onClick={() => setRadioValue(value)}
      />
      <Flex direction="column" gap="8px" userSelect="none" cursor="pointer">
        <Text as="p" textStyle="h6Regular">
          {title}
        </Text>
        <Text as="small" textStyle="regular" userSelect="none">
          {description}
        </Text>
        {children}
      </Flex>
    </Flex>
  );
}

function AddBenefitNoCategoriesAssignedModal({
  isOpen,
  onClose,
  onSave,
  options = [],
}: AddBenefitNoCategoriesAssignedModalProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [radioValue, setRadioValue] = useState<'uncategorized' | 'addCategory'>('uncategorized');

  const clearFields = () => {
    setSelected([]);
    setRadioValue('uncategorized');
  };

  const handleClose = () => {
    onClose();
    clearFields();
  };

  // reset fields from parent component when modal is closed
  useEffect(() => {
    if (!isOpen) {
      clearFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = () => {
    if (radioValue === 'addCategory') {
      onSave(selected);
    } else {
      onSave([]); // empty array for uncategorized
    }
  };

  const handleChange = (ids: string[]) => {
    setSelected(ids);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">No categories assigned</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircle boxSize="30px" />}
              aria-label="Close no categories assigned to benefit modal"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <FormControl isRequired>
            <RadioGroup
              value={radioValue}
              onChange={(val) => setRadioValue(val as 'uncategorized' | 'addCategory')}
            >
              <Flex direction="column" gap="16px">
                <RadioOption
                  title="Leave unassigned"
                  description={`The benefit will appear under the "General" group`}
                  value="uncategorized"
                  setRadioValue={setRadioValue}
                  radioValue={radioValue}
                />

                <RadioOption
                  title="Assign categories"
                  description="Select one or more categories before publishing"
                  value="addCategory"
                  setRadioValue={setRadioValue}
                  radioValue={radioValue}
                >
                  {radioValue === 'addCategory' && (
                    <DropdownMultiSelectInput
                      options={options}
                      selected={selected}
                      onChange={handleChange}
                      placeholder="Choose category"
                    />
                  )}
                </RadioOption>
              </Flex>
            </RadioGroup>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button
            size="md"
            leftIcon={<CheckOutline boxSize="20px" />}
            onClick={handleSave}
            isDisabled={radioValue === 'addCategory' && selected.length === 0}
          >
            Publish
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddBenefitNoCategoriesAssignedModal;
